import {useCallback, useEffect, useState} from 'react'

import PropTypes from 'prop-types'

import {useSuiContext} from '@s-ui/react-context'
import IconImageMultipleOutline from '@adv-mt/mt-svg-icons/lib/ImageMultipleOutline'
import IconPlaySolid from '@adv-mt/mt-svg-icons/lib/PlaySolid'
import {BUTTON_VARIANTS} from '@adv-mt/ui/button'
import {Icon, ICON_SIZES} from '@adv-mt/ui/icon'
import {LOADING, MediaImage, OBJECT_FIT} from '@adv-mt/ui/media-image'
import {MediaVideo, VIDEO_PROVIDERS} from '@adv-mt/ui/media-video'

import {BASE_CLASS, MEDIA_TYPES, mediaItemShape} from './config.js'
import GalleryIconButton from './GalleryIconButton.js'

const GalleryMediaItem = ({
  item,
  alt,
  index,
  className = `${BASE_CLASS}-media`,
  // COVER by default: the desktop grid has fixed-size cells to fill
  objectFit = OBJECT_FIT.COVER,
  loading: loadingProp,
  isPriority = false,
  isInteractive = true,
  // Carousels keep neighbouring slides mounted, so being mounted isn't the same as being on
  // screen. Only the active slide is allowed to hold a running player.
  isActive = true,
  hasVideoControls = false,
  shouldAutoPlayVideo = false,
  canPlayInline = false,
  useVideoOverlay = false,
  onOpenGallery,
  onVideoButtonClick,
  onVideoPlayingChange
}) => {
  const {i18n} = useSuiContext()
  // null = let item.autoPlay decide; true = user pressed play; false = user closed.
  // A plain boolean can't represent "closed" for autoPlay items — they'd reopen immediately.
  const [inlineOverride, setInlineOverride] = useState(null)
  const isVideo = item.type === MEDIA_TYPES.VIDEO
  const isInlinePlayer = canPlayInline && (inlineOverride ?? Boolean(item.autoPlay))
  const shouldAutoPlay = hasVideoControls ? shouldAutoPlayVideo : true
  const isAmbientAutoPlay = !hasVideoControls && item.autoPlay
  const hasPlayer = isVideo && isActive && (hasVideoControls || isInlinePlayer)

  const handlePlayVideo = useCallback(() => onOpenGallery?.(index, {play: true}), [onOpenGallery, index])
  const handleStartInline = useCallback(() => {
    onVideoButtonClick?.()
    setInlineOverride(true)
  }, [onVideoButtonClick])
  const handleOpen = useCallback(() => onOpenGallery?.(index), [onOpenGallery, index])
  const handleVideoPlay = useCallback(() => onVideoPlayingChange?.(true), [onVideoPlayingChange])
  const handleVideoStop = useCallback(() => onVideoPlayingChange?.(false), [onVideoPlayingChange])

  const handleCloseInline = useCallback(() => {
    setInlineOverride(false)
    handleVideoStop()
  }, [handleVideoStop])

  useEffect(() => {
    if (!isVideo || isActive) return
    setInlineOverride(false)
    handleVideoStop()
  }, [isVideo, isActive, handleVideoStop])

  useEffect(() => {
    if (!hasPlayer || !shouldAutoPlay) return
    handleVideoPlay()
  }, [hasPlayer, shouldAutoPlay, handleVideoPlay])

  useEffect(() => (isVideo ? handleVideoStop : undefined), [isVideo, handleVideoStop])

  const loading = loadingProp ?? (isPriority ? LOADING.EAGER : LOADING.LAZY)

  if (isVideo) {
    if (hasPlayer) {
      const player = (
        <MediaVideo
          src={item.src}
          controls
          provider={item.provider}
          poster={item.poster}
          autoPlay={shouldAutoPlay}
          muted={isAmbientAutoPlay}
          className={className}
          onPlay={handleVideoPlay}
          onPause={handleVideoStop}
          onEnded={handleVideoStop}
        />
      )

      if (!isInlinePlayer) return player

      const isIframeBased = item.provider === VIDEO_PROVIDERS.YOUTUBE || item.provider === VIDEO_PROVIDERS.VIMEO
      if (!useVideoOverlay || !isIframeBased) return player

      return (
        <div className={`${BASE_CLASS}-video-player`}>
          {player}
          <button
            type="button"
            className={`${BASE_CLASS}-video-stop`}
            onClick={handleCloseInline}
            aria-label={i18n.t('COMMON.GALLERY.CLOSE_VIDEO')}
          />
        </div>
      )
    }

    // Static poster + play button. Inline slots start the player in place; the desktop
    // non-featured slot opens the fullscreen modal with the video autoplaying.
    return (
      <div className={`${BASE_CLASS}-video-thumbnail`}>
        {item.poster && (
          <MediaImage
            src={item.poster}
            alt=""
            objectFit={objectFit}
            loading={loading}
            priority={isPriority}
            className={className}
          />
        )}
        <span className={`${BASE_CLASS}-video-play`}>
          <GalleryIconButton
            icon={IconPlaySolid}
            variant={BUTTON_VARIANTS.SOLID}
            onClick={canPlayInline ? handleStartInline : handlePlayVideo}
            aria-label={i18n.t('COMMON.GALLERY.PLAY_VIDEO')}
          />
        </span>
      </div>
    )
  }

  if (item.type === MEDIA_TYPES.PLACEHOLDER) {
    return (
      <Icon size={ICON_SIZES.CURRENT} className={`${BASE_CLASS}-placeholder`}>
        <IconImageMultipleOutline size={ICON_SIZES.CURRENT} />
      </Icon>
    )
  }

  const image = (
    <MediaImage
      src={item.src}
      alt={alt}
      objectFit={objectFit}
      loading={loading}
      priority={isPriority}
      className={className}
    />
  )

  if (!isInteractive) return image

  return (
    <button
      type="button"
      className={`${BASE_CLASS}-media-action`}
      onClick={handleOpen}
      aria-label={`${i18n.t('COMMON.GALLERY.OPEN')} ${index + 1}`}
    >
      {image}
    </button>
  )
}

GalleryMediaItem.displayName = 'GalleryMediaItem'

GalleryMediaItem.propTypes = {
  item: mediaItemShape.isRequired,
  alt: PropTypes.string,
  index: PropTypes.number,
  className: PropTypes.string,
  objectFit: PropTypes.string,
  loading: PropTypes.string,
  isPriority: PropTypes.bool,
  isInteractive: PropTypes.bool,
  isActive: PropTypes.bool,
  hasVideoControls: PropTypes.bool,
  shouldAutoPlayVideo: PropTypes.bool,
  canPlayInline: PropTypes.bool,
  useVideoOverlay: PropTypes.bool,
  onOpenGallery: PropTypes.func,
  onVideoButtonClick: PropTypes.func,
  onVideoPlayingChange: PropTypes.func
}

export default GalleryMediaItem
